import { User } from 'firebase/auth';

export interface Property {
  city: string;
  cityLabel: string;
  neighborhood: string;
  neighborhoodLabel: string;
  street: string;
  streetLabel: string;
  houseNumber: string;
  rooms: number;
  floor: number;
  bathrooms: number;
  parkingSpots: number;
  size: number;
  price: number;
  advisorPrice?: number;
  propertyType: string;
  propertyTypeLabel: string;
  description?: string;
  email: string;
  images: File[];
  propertyAge: string;
  propertyAgeLabel: string;
  propertyCondition?: string;
  propertyConditionLabel?: string;
  view: string;
  viewLabel: string;
  airDirection: string;
  airDirectionLabel: string;
  hasBalcony: boolean;
  balconySize?: number;
  hasElevator: boolean;
  hasSaferoom: boolean;
  daysOnMarket: string | number;
}

export interface Option {
  value: string;
  label: string;
}

export interface StreetsByNeighborhood {
  [key: string]: Array<{ value: string; label: string }>;
}

export interface NearbyPlace {
  name: string;
  address: string;
  type: string;
  label: string;
  rating: number;
  distance: number;
}

export interface SubscriptionPlan {
  id: string;
  name: string;
  reports: number;
  price: number;
}

export interface UserSubscription {
  planId: string;
  reportsTotal: number;
  reportsUsed: number;
  purchaseDate: string;
  adsTotal: number;
  adsUsed: number;
}

export interface UserProfile {
  id: string;
  fullName: string;
  email: string;
  phone: string;
  branchName: string;
  createdAt: string;
  updatedAt: string;
  role: string;
  subscription: UserSubscription;
  logoUrl?: string;
  brokerLicense?: string;
  createAdCount?: number;
}

export interface ReportHistoryItem {
  formId: string;
  reportId?: string;
  generatedAt?: string;
  lastModified?: string;
  version: number;
  status: 'draft' | 'completed';
  createdAt?: string;
  agentId?: string | null;
  agentName?: string | null;
  address: {
    city: string;
    neighborhood?: string;
    street: string;
    houseNumber: string;
  };
}

export interface SearchCriteria {
  propertyTypes?: string[];
  priceMin?: number;
  priceMax?: number;
  priceTolerance?: number;
  rooms?: number[];
  cities?: string[];
  neighborhoods?: string[];
  minSize?: number;
  maxSize?: number;
}

export interface Client {
  id: string;
  userId: string;
  name: string;
  phone: string;
  email?: string;
  searchCriteria: SearchCriteria;
  notes?: string;
  leadType?: 'buyer' | 'seller';
  customerStatus?: string;
  lastContactAt?: string | null;
  lastActivityAt?: string | null;
  nextFollowUpAt?: string | null;
  nextAction?: string | null;
  lastActivity?: { activityType: string; createdAt: string } | null;
  agentId?: string | null;
  agentName?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface PropertyClientMatch {
  id: string;
  clientId: string;
  formId: string;
  userId: string;
  status: 'new' | 'interested' | 'viewed' | 'offer_made' | 'rejected' | 'closed';
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

export interface MatchAlert {
  id: string;
  office_id: string;
  buyer_id: string;
  property_id: string;
  buyer_agent_id: string;
  property_agent_id: string;
  match_score: number;
  rank_position: number;
  alert_type: 'buyer_new' | 'buyer_updated' | 'property_new' | 'property_updated';
  status: 'new' | 'viewed' | 'dismissed' | 'resolved';
  buyer_name: string | null;
  property_address: string | null;
  created_at: string;
  updated_at: string;
  viewed_at: string | null;
}

// ── Commercial Entitlement Types ──────────────────────────────

export type SubscriptionStatus =
  | 'trial_active'
  | 'active'
  | 'trial_expired'
  | 'past_due'
  | 'cancelled'
  | 'internal';

export type EntitlementResolutionState = 'loading' | 'resolved' | 'error';

export type AccountType = 'brokerage' | 'developer';

export interface OfficeEntitlement {
  office_id: string | null;
  office_name: string | null;
  my_role: string | null;
  account_type: AccountType;
  subscription_status: SubscriptionStatus | null;
  is_internal: boolean;
  is_trial_active: boolean;
  is_active: boolean;
  is_trial_expired: boolean;
  is_expired: boolean;
  can_access_app: boolean;
  can_write: boolean;
  can_use_ai: boolean;
  can_use_matching: boolean;
  can_use_smart_alerts: boolean;
  can_view_property_score: boolean;
  can_view_business_data: boolean;
  can_generate_ads: boolean;
  can_generate_reports: boolean;
  can_manage_members: boolean;
  is_read_only: boolean;
  seat_limit: number | null;
  active_seat_count: number | null;
  remaining_seats: number | null;
  trial_ends_at: string | null;
  trial_days_remaining: number | null;
  billing_required: boolean;
  features: Record<string, boolean>;
  plan_id: string | null;
}

export const DEFAULT_ENTITLEMENT: OfficeEntitlement = {
  office_id: null,
  office_name: null,
  my_role: null,
  account_type: 'brokerage',
  subscription_status: null,
  is_internal: false,
  is_trial_active: false,
  is_active: true,
  is_trial_expired: false,
  is_expired: false,
  can_access_app: true,
  can_write: true,
  can_use_ai: true,
  can_use_matching: true,
  can_use_smart_alerts: true,
  can_view_property_score: true,
  can_view_business_data: true,
  can_generate_ads: true,
  can_generate_reports: true,
  can_manage_members: false,
  is_read_only: false,
  seat_limit: null,
  active_seat_count: null,
  remaining_seats: null,
  trial_ends_at: null,
  trial_days_remaining: null,
  billing_required: false,
  features: { all: true },
  plan_id: null,
};

/**
 * Fail-closed entitlement — used when the entitlement service cannot be reached.
 * All paid/write/AI capabilities are false. The user can still see the app shell
 * (can_access_app = true) so the frontend can show a retry/error screen, but
 * NO commercial capabilities are granted based on a service failure.
 *
 * This is NOT the same as DEFAULT_ENTITLEMENT (which represents a successfully
 * resolved "no office" state with legacy compatibility).
 */
export const FAIL_CLOSED_ENTITLEMENT: OfficeEntitlement = {
  office_id: null,
  office_name: null,
  my_role: null,
  account_type: 'brokerage',
  subscription_status: null,
  is_internal: false,
  is_trial_active: false,
  is_active: false,
  is_trial_expired: false,
  is_expired: false,
  can_access_app: true, // allow app shell so user can see retry screen
  can_write: false,
  can_use_ai: false,
  can_use_matching: false,
  can_use_smart_alerts: false,
  can_view_property_score: false,
  can_view_business_data: false,
  can_generate_ads: false,
  can_generate_reports: false,
  can_manage_members: false,
  is_read_only: true,
  seat_limit: null,
  active_seat_count: null,
  remaining_seats: null,
  trial_ends_at: null,
  trial_days_remaining: null,
  billing_required: false,
  features: {},
  plan_id: null,
};

export interface FormData {
  formId: string;
  userId: string;
  ownerDisplayName?: string | null;
  isOwnProperty?: boolean;
  createdAt: string;
  reportId?: string;
  generatedAt?: string;
  lastModified?: string;
  version: number;
  status: 'draft' | 'completed';
  address: {
    city: string;
    neighborhood: string;
    street: string;
    houseNumber: string;
  };
  property: Property;
  imageUrls: {
    propertyImages: string[];
    streetView?: string;
    staticMap?: string;
  };
  nearbyPlaces: Record<string, NearbyPlace[]>;
}